import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Search, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Filter,
  CreditCard,
  CalendarClock,
  CheckCircle2,
  AlertTriangle,
  Users,
  ArrowUpRight,
  FileText,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  TrendingUp,
  Activity,
  BarChart3,
  Package,
  Star,
  DollarSign,
  Calendar,
  CheckCircle
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle, 
  CardFooter 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { LoadingScreen } from '@/components/common';

interface Subscription {
  id: string;
  display_id: string;
  state: string;
  start_date: string;
  end_date: string | null;
  canceled_at: string | null;
  currency_code: string | null;
  po_number: string | null;
  next_billing_at: string | null;
  payment_source_id: string | null;
  net_term_days: number | null;
  auto_renew: boolean;
  related_subscription: string | null;
  renewed_at: string | null;
  subscription_type: 'main' | 'child';
  parent_subscription_id: string | null;
  subscription_customers: {
    id: string;
    customer_role: 'owner' | 'participant' | 'billing_contact';
    customer: {
      id: string;
    name: string;
      legal_name: string;
    domain?: string;
    };
  }[];
  parent_subscription?: {
    id: string;
    display_id: string;
  };
  child_subscriptions_count?: number;
  consolidated_total?: number;
  items: {
    id: string;
    subscription_plan: {
      name: string;
      billing_period_type: string;
      prices?: {
        currency: string;
        price_amount: number;
      }[];
    };
    quantity: number;
    unit_price: number;
  }[];
}

interface CustomerOption {
  id: string;
  name: string;
  domain?: string;
}

const Subscriptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customer');
  
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  
  // Search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>(' ');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Expanded rows for subscription items
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Redirect to customer detail page if customer parameter is present
  useEffect(() => {
    if (customerId) {
      navigate(`/customers/${customerId}`);
    }
  }, [customerId, navigate]);

  useEffect(() => {
    fetchSubscriptions();
    fetchCustomers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [subscriptions, searchTerm, statusFilter, customerFilter, showInactive]);

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, domain')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching customers:', error);
        throw error;
      }
      
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Não foi possível carregar os clientes.');
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          subscription_customers (
            id,
            customer_role,
            customer:customers (
              id,
              name,
              legal_name,
              domain
            )
          ),
          parent_subscription:parent_subscription_id (
            id,
            display_id
          ),
          items:subscription_items (
            id,
            subscription_plan_id,
            quantity,
            unit_price
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Subscription fetch error:', error);
        throw error;
      }
      
      // Process subscription data
      const processedData = await Promise.all((data || []).map(async (sub) => {
        // Count child subscriptions if this is a main subscription
        let childSubscriptionsCount = 0;
        let consolidatedTotal = 0;
        
        if (sub.subscription_type === 'main') {
          const { count } = await supabase
            .from('subscriptions')
            .select('*', { count: 'exact', head: true })
            .eq('parent_subscription_id', sub.id);
          childSubscriptionsCount = count || 0;
          
          // If there are child subscriptions, get their values for consolidated total
          if (childSubscriptionsCount > 0) {
            const { data: childSubscriptions, error: childError } = await supabase
              .from('subscriptions')
              .select(`
                id,
                currency_code,
                items:subscription_items (
                  quantity,
                  unit_price
                )
              `)
              .eq('parent_subscription_id', sub.id);
              
            if (!childError && childSubscriptions) {
              consolidatedTotal = childSubscriptions.reduce((total, childSub) => {
                const childTotal = childSub.items.reduce((itemTotal, item) => {
                  return itemTotal + (item.unit_price * item.quantity);
                }, 0);
                return total + childTotal;
              }, 0);
            }
          }
        }

        // For each subscription item, get the subscription plan details
        let itemsWithPlans = [];
        if (sub.items && sub.items.length > 0) {
          const planIds = sub.items.map(item => item.subscription_plan_id);
          const { data: plansData } = await supabase
            .from('subscription_plans')
            .select(`
              id, 
              name, 
              billing_period_type,
              prices:subscription_plan_prices (
                currency,
                price_amount
              )
            `)
            .in('id', planIds);
            
          // Map plan data to each item
          itemsWithPlans = sub.items.map(item => {
            const plan = plansData?.find(p => p.id === item.subscription_plan_id);
            return {
              ...item,
              subscription_plan: {
                name: plan?.name || 'Unknown Plan',
                billing_period_type: plan?.billing_period_type || 'monthly',
                prices: plan?.prices || []
              }
            };
          });
        }
        
        return {
          ...sub,
          child_subscriptions_count: childSubscriptionsCount,
          consolidated_total: consolidatedTotal,
          items: itemsWithPlans
        };
      }));
      
      setSubscriptions(processedData as Subscription[]);
      setFilteredSubscriptions(processedData as Subscription[]);
      setTotalPages(Math.ceil((processedData?.length || 0) / pageSize));
        
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      toast.error('Não foi possível carregar as assinaturas.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for new subscription structure
  const getOwnerCustomer = (subscription: Subscription) => {
    return subscription.subscription_customers?.find(sc => sc.customer_role === 'owner')?.customer;
  };

  const getParticipantsCount = (subscription: Subscription) => {
    return subscription.subscription_customers?.filter(sc => sc.customer_role === 'participant').length || 0;
  };

  const getSubscriptionTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'main': 'Principal',
      'child': 'Filha'
    };
    return types[type] || type;
  };

  // Function to organize subscriptions hierarchically
  const organizeHierarchically = (subs: Subscription[]) => {
    const result: Subscription[] = [];
    const mainSubscriptions = subs.filter(sub => sub.subscription_type === 'main');
    const childSubscriptions = subs.filter(sub => sub.subscription_type === 'child');
    
    // Group child subscriptions by parent
    const childrenByParent = childSubscriptions.reduce((acc, child) => {
      const parentId = child.parent_subscription_id;
      if (parentId) {
        if (!acc[parentId]) acc[parentId] = [];
        acc[parentId].push(child);
      }
      return acc;
    }, {} as Record<string, Subscription[]>);
    
    // Add main subscriptions followed by their children
    mainSubscriptions.forEach(main => {
      result.push(main);
      const children = childrenByParent[main.id] || [];
      // Sort children by created_at or display_id
      children.sort((a, b) => {
        if (a.display_id && b.display_id) {
          return a.display_id.localeCompare(b.display_id);
        }
        return a.id.localeCompare(b.id);
      });
      result.push(...children);
    });
    
    // Add orphaned child subscriptions (those without a main parent in current list)
    const orphanedChildren = childSubscriptions.filter(child => 
      !mainSubscriptions.some(main => main.id === child.parent_subscription_id)
    );
    result.push(...orphanedChildren);
    
    return result;
  };

  const applyFilters = () => {
    let filtered = [...subscriptions];
    
    // Apply text search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(sub => {
        const ownerCustomer = getOwnerCustomer(sub);
        return sub.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
               sub.display_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               ownerCustomer?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               sub.items.some(item => item.subscription_plan.name.toLowerCase().includes(searchTerm.toLowerCase()));
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.state.toLowerCase() === statusFilter.toLowerCase());
    }
    
    // Apply inactive filter
    if (!showInactive) {
      filtered = filtered.filter(sub => 
        sub.state.toLowerCase() !== 'canceled' && 
        sub.state.toLowerCase() !== 'expired'
      );
    }
    
    // Apply customer filter (by owner customer ID)
    if (customerFilter && customerFilter.trim() !== '') {
      filtered = filtered.filter(sub => {
        const ownerCustomer = getOwnerCustomer(sub);
        return ownerCustomer?.id === customerFilter;
      });
    }
    
    // Organize hierarchically (main + children grouped together)
    const organizedFiltered = organizeHierarchically(filtered);
    
    setFilteredSubscriptions(organizedFiltered);
    setCurrentPage(1);
    setTotalPages(Math.ceil(organizedFiltered.length / pageSize));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCustomerFilter(' '); // Changed from empty string to space
    setShowInactive(false);
  };

  const toggleExpanded = (subscriptionId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(subscriptionId)) {
      newExpandedRows.delete(subscriptionId);
    } else {
      newExpandedRows.add(subscriptionId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSubscriptionClick = (subscriptionId: string) => {
    navigate(`/subscriptions/${subscriptionId}`);
  };

  const getBillingFrequency = (periodType: string) => {
    const periodMap: Record<string, string> = {
      monthly: 'Mensal',
      month: 'mês',
      yearly: 'Anual',
      year: 'ano',
      quarterly: 'Trimestral',
      semiannually: 'Semestral',
      weekly: 'Semanal',
      daily: 'Diário',
      'one-time': 'Pagamento Único',
    };
    
    return periodMap[periodType.toLowerCase()] || periodType;
  };

  // Function to get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredSubscriptions.slice(startIndex, endIndex);
  };

  // Function to get customer's initials for avatar
  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Function to handle image loading errors
  const handleImageError = (customerId: string) => {
    // In a real implementation, you would track failed images
    console.log(`Failed to load image for customer ${customerId}`);
  };

  const getStatsCards = () => {
    // Calculate subscription statistics
    const totalSubscriptions = subscriptions.length;
    const activeSubscriptions = subscriptions.filter(sub => sub.state.toLowerCase() === 'active').length;
    const trialSubscriptions = subscriptions.filter(sub => sub.state.toLowerCase() === 'trial').length;
    const pendingSubscriptions = subscriptions.filter(sub => sub.state.toLowerCase() === 'pending').length;
    const canceledSubscriptions = subscriptions.filter(sub => sub.state.toLowerCase() === 'canceled').length;
    
    const monthlyRecurringRevenue = subscriptions
      .filter(sub => sub.state.toLowerCase() === 'active')
      .reduce((total, sub) => {
        return total + sub.items.reduce((itemTotal, item) => {
          // Adjust for billing frequency
          const multiplier = 
            item.subscription_plan.billing_period_type === 'yearly' ? (1/12) :
            item.subscription_plan.billing_period_type === 'quarterly' ? (1/3) :
            item.subscription_plan.billing_period_type === 'semiannually' ? (1/6) : 1;
          
          return itemTotal + (item.unit_price * item.quantity * multiplier);
        }, 0);
      }, 0);
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-md">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Total de Assinaturas</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {totalSubscriptions}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Registradas no sistema
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-blue-500" />
                <span className="text-xs text-green-600 font-extrabold">+8%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-md">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Assinaturas Ativas</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {activeSubscriptions}
              </div>
            </div>
            <div className="space-y-2">
              <Progress 
                value={totalSubscriptions > 0 ? (activeSubscriptions / totalSubscriptions) * 100 : 0} 
                className="h-1.5"
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {totalSubscriptions > 0 ? `${Math.round((activeSubscriptions / totalSubscriptions) * 100)}% do total` : '0% do total'}
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600 font-extrabold">+12%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 rounded-md">
                  <DollarSign className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">MRR</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {formatCurrency(monthlyRecurringRevenue, 'BRL')}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Receita mensal recorrente
              </div>
              <div className="flex items-center gap-1">
                <BarChart3 className="h-3 w-3 text-purple-500" />
                <span className="text-xs text-purple-600 font-extrabold">+15%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-orange-100 rounded-md">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Canceladas</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {canceledSubscriptions}
              </div>
            </div>
            <div className="space-y-2">
              <Progress 
                value={totalSubscriptions > 0 ? (canceledSubscriptions / totalSubscriptions) * 100 : 0} 
                className="h-1.5"
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {totalSubscriptions > 0 ? `${Math.round((canceledSubscriptions / totalSubscriptions) * 100)}% do total` : '0% do total'}
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-orange-500 rotate-180" />
                  <span className="text-xs text-orange-600 font-extrabold">-3%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return <LoadingScreen message="Carregando Assinaturas..." size="x-small" />;
  }

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Assinaturas</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Header with Gradient Background */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Assinaturas</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie e acompanhe todas as assinaturas dos seus clientes
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={fetchSubscriptions}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
                <Button 
                  size="default" 
                  onClick={() => navigate('/subscription-wizard/step1')}
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Assinatura
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Enhanced Summary Cards */}
            {getStatsCards()}

            {/* Enhanced Main Content */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                {/* Enhanced Filter Section */}
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="relative min-w-0 flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                      <Input
                        placeholder="Pesquisar assinaturas..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                      {searchTerm && (
                        <button
                          className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setSearchTerm('')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="min-w-0 flex-1 max-w-xs">
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os status</SelectItem>
                          <SelectItem value="active">Ativo</SelectItem>
                          <SelectItem value="trial">Teste</SelectItem>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="canceled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="min-w-0 flex-1 max-w-xs">
                      <Select value={customerFilter} onValueChange={setCustomerFilter}>
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Cliente" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value=" ">Todos os clientes</SelectItem>
                          {customers.map(customer => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-10 px-4 border-gray-200 hover:bg-gray-50">
                          <Filter className="h-4 w-4 mr-2" />
                          Filtros
                          {(showInactive) && (
                            <Badge variant="secondary" className="ml-2 h-4 w-4 p-0 text-xs">
                              1
                            </Badge>
                          )}
                          <ChevronDown className="h-3 w-3 ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-6" align="end">
                        <div className="space-y-6">
                          <div>
                            <h4 className="font-extrabold text-gray-900 mb-1">Filtros Avançados</h4>
                            <p className="text-sm text-gray-500">Personalize a visualização das assinaturas</p>
                          </div>
                          
                          <Separator />
                          
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <label className="text-sm font-extrabold text-gray-700">
                                  Mostrar assinaturas inativas
                                </label>
                                <p className="text-xs text-gray-500">
                                  Incluir assinaturas canceladas na listagem
                                </p>
                              </div>
                              <Checkbox 
                                checked={showInactive} 
                                onCheckedChange={(checked) => setShowInactive(!!checked)} 
                              />
                            </div>
                          </div>
                          
                          <div className="flex justify-between pt-4 border-t">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={clearFilters}
                            >
                              Limpar Filtros
                            </Button>
                            <Button size="sm" onClick={() => setIsFilterOpen(false)}>
                              Aplicar
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Content Area */}
                {filteredSubscriptions.length > 0 ? (
                  <div className="space-y-6">
                    <div className="overflow-hidden">
                      <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-gray-50">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-b border-gray-200">
                              <TableHead className="w-[80px] text-gray-600 font-extrabold"></TableHead>
                              <TableHead className="text-gray-600 font-extrabold">ID da Assinatura</TableHead>
                              <TableHead className="text-gray-600 font-extrabold">Cliente</TableHead>
                              <TableHead className="text-gray-600 font-extrabold">Hierarquia</TableHead>
                              <TableHead className="text-gray-600 font-extrabold">Status</TableHead>
                              <TableHead className="text-gray-600 font-extrabold">Início</TableHead>
                              <TableHead className="text-right text-gray-600 font-extrabold">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {getCurrentPageItems().map((subscription) => (
                              <React.Fragment key={subscription.id}>
                                <TableRow 
                                  className={`group cursor-pointer transition-all duration-200 hover:bg-blue-50/50 ${
                                    subscription.subscription_type === 'child' 
                                      ? 'bg-blue-50/30 hover:bg-blue-50/60 border-l-2 border-l-blue-300' 
                                      : 'hover:bg-gray-50/80'
                                  }`}
                                  onClick={() => handleSubscriptionClick(subscription.id)}
                                >
                                  <TableCell>
                                    <div className={`flex items-center gap-2 ${
                                      subscription.subscription_type === 'child' ? 'ml-4' : ''
                                    }`}>
                                      {subscription.subscription_type === 'child' && (
                                        <div className="flex items-center justify-center w-4 h-4 text-blue-500">
                                          <ArrowDown className="h-3 w-3" />
                                        </div>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0 hover:bg-blue-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleExpanded(subscription.id);
                                        }}
                                      >
                                        {expandedRows.has(subscription.id) ? (
                                          <ChevronUp className="h-4 w-4 text-blue-600" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4 text-gray-500" />
                                        )}
                                      </Button>
                                      <Avatar className="h-10 w-10 border border-gray-200 shadow-sm">
                                        {getOwnerCustomer(subscription)?.domain ? (
                                          <AvatarImage 
                                            src={`https://cdn.brandfetch.io/${getOwnerCustomer(subscription)?.domain}?c=1idk4AUs2-buvooOUvQ`}
                                            alt={getOwnerCustomer(subscription)?.name}
                                            onError={() => handleImageError(getOwnerCustomer(subscription)?.id || '')}
                                          />
                                        ) : (
                                          <AvatarFallback className="bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 font-extrabold">
                                            {getInitials(getOwnerCustomer(subscription)?.name || '')}
                                          </AvatarFallback>
                                        )}
                                      </Avatar>
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-extrabold">
                                    <div className="font-mono text-sm text-gray-700">
                                      {subscription.display_id || `#${subscription.id.substring(0, 8)}`}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-extrabold">
                                    <div>
                                      <div className="font-extrabold text-gray-900">
                                        {getOwnerCustomer(subscription)?.name || 'N/A'}
                                      </div>
                                      {getOwnerCustomer(subscription)?.domain && (
                                        <div className="text-xs text-gray-500">
                                          {getOwnerCustomer(subscription)?.domain}
                                        </div>
                                      )}
                                      {getParticipantsCount(subscription) > 0 && (
                                        <div className="text-xs text-blue-600 font-extrabold">
                                          +{getParticipantsCount(subscription)} participante(s)
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-1">
                                        {subscription.subscription_type === 'child' && (
                                          <ArrowUp className="h-3 w-3 text-blue-500" />
                                        )}
                                        <Badge 
                                          variant={subscription.subscription_type === 'main' ? 'default' : 'secondary'} 
                                          className={`text-xs ${
                                            subscription.subscription_type === 'child' 
                                              ? 'bg-blue-100 text-blue-700 border-blue-200' 
                                              : 'bg-gray-100 text-gray-700 border-gray-200'
                                          }`}
                                        >
                                          {getSubscriptionTypeLabel(subscription.subscription_type)}
                                        </Badge>
                                      </div>
                                      {subscription.subscription_type === 'main' && subscription.child_subscriptions_count > 0 && (
                                        <div className="text-xs text-green-600 font-extrabold">
                                          {subscription.child_subscriptions_count} filha(s)
                                        </div>
                                      )}
                                      {subscription.subscription_type === 'child' && subscription.parent_subscription && (
                                        <div className="text-xs text-blue-600 font-extrabold flex items-center gap-1">
                                          <span>↳ {subscription.parent_subscription.display_id}</span>
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <StatusBadge status={subscription.state} context="subscription" />
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm text-gray-700">
                                      {formatDate(subscription.start_date)}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8 hover:bg-blue-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/subscriptions/${subscription.id}`);
                                        }}
                                      >
                                        <ArrowUpRight className="h-4 w-4 text-blue-600" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                
                                {/* Enhanced Expanded section for subscription items */}
                                {expandedRows.has(subscription.id) && (
                                  <TableRow>
                                    <TableCell colSpan={7} className="p-0">
                                      <div className="bg-gradient-to-r from-blue-50/50 to-gray-50/50 border-t border-blue-100">
                                        <div className="p-6">
                                          {subscription.items.length > 0 ? (
                                            <div className="space-y-4">
                                              <div className="flex items-center gap-2 mb-4">
                                                <Package className="h-4 w-4 text-blue-600" />
                                                <h4 className="font-extrabold text-gray-900">Itens da Assinatura</h4>
                                              </div>
                                              
                                              <div className="grid gap-3">
                                                {subscription.items.map((item, index) => (
                                                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                    <div className="grid gap-4" style={{ gridTemplateColumns: '2fr 1fr 1.2fr 1.2fr 1fr 1.2fr 1fr' }}>
                                                      <div>
                                                        <div className="font-extrabold text-gray-900 mb-1">
                                                          {item.subscription_plan.name}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                          Plano de {getBillingFrequency(item.subscription_plan.billing_period_type)}
                                                        </div>
                                                      </div>
                                                      <div className="text-center">
                                                        <div className="text-sm font-extrabold text-gray-900">
                                                          {item.quantity}
                                                        </div>
                                                        <div className="text-xs text-gray-500">Quantidade</div>
                                                      </div>
                                                      <div className="text-right">
                                                        <div className="text-sm font-extrabold text-gray-900">
                                                          {formatCurrency(item.unit_price, subscription.currency_code || 'BRL')}
                                                        </div>
                                                        <div className="text-xs text-gray-500">Valor Unit.</div>
                                                      </div>
                                                      <div className="text-right">
                                                        <div className="text-sm font-extrabold text-blue-600">
                                                          {formatCurrency(item.unit_price * item.quantity, subscription.currency_code || 'BRL')}
                                                        </div>
                                                        <div className="text-xs text-gray-500">Total</div>
                                                      </div>
                                                      <div className="text-center">
                                                        <Badge variant="outline" className="text-xs">
                                                          {getBillingFrequency(item.subscription_plan.billing_period_type)}
                                                        </Badge>
                                                      </div>
                                                      <div className="text-center">
                                                        <div className="text-sm text-gray-700">
                                                          {formatDate(subscription.start_date)}
                                                        </div>
                                                      </div>
                                                      <div className="text-center">
                                                        <StatusBadge status={subscription.state} context="subscription" />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="text-center py-8">
                                              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                              <p className="text-sm text-gray-500">Nenhum item encontrado nesta assinatura</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </Card>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="space-y-6">
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                        <CreditCard className="h-12 w-12 text-blue-500" />
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xl font-extrabold text-gray-900">
                          Nenhuma assinatura encontrada
                        </h3>
                        <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                          Não encontramos assinaturas que correspondam aos critérios de busca. 
                          Tente ajustar os filtros ou criar uma nova assinatura.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button variant="outline" onClick={clearFilters} size="lg">
                          <Filter className="h-5 w-5 mr-2" />
                          Limpar Filtros
                        </Button>
                        <Button onClick={() => navigate('/subscription-wizard/step1')} size="lg">
                          <Plus className="h-5 w-5 mr-2" />
                          Nova Assinatura
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Enhanced Modern Pagination */}
                {filteredSubscriptions.length > 0 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">Mostrar:</span>
                        <Select
                          value={pageSize.toString()}
                          onValueChange={(value) => {
                            setPageSize(Number(value));
                            setCurrentPage(1);
                            setTotalPages(Math.ceil(filteredSubscriptions.length / Number(value)));
                          }}
                        >
                          <SelectTrigger className="h-8 w-[80px] bg-white border-gray-200">
                            <SelectValue placeholder={pageSize.toString()} />
                          </SelectTrigger>
                          <SelectContent side="top">
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-sm text-gray-600">
                        <span className="font-extrabold">{Math.min((currentPage - 1) * pageSize + 1, filteredSubscriptions.length)}</span>-
                        <span className="font-extrabold">{Math.min(currentPage * pageSize, filteredSubscriptions.length)}</span> de{" "}
                        <span className="font-extrabold">{filteredSubscriptions.length}</span> resultado(s)
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className="bg-white border-gray-200 hover:bg-gray-50"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Anterior
                      </Button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNumber = i + 1;
                          return (
                            <Button
                              key={pageNumber}
                              variant={currentPage === pageNumber ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNumber)}
                              className={`w-8 h-8 p-0 ${
                                currentPage === pageNumber 
                                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </Button>
                          );
                        })}
                        {totalPages > 5 && (
                          <>
                            <span className="text-gray-500">...</span>
                            <Button
                              variant={currentPage === totalPages ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(totalPages)}
                              className={`w-8 h-8 p-0 ${
                                currentPage === totalPages 
                                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {totalPages}
                            </Button>
                          </>
                        )}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="bg-white border-gray-200 hover:bg-gray-50"
                      >
                        Próximo
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Bottom Spacing */}
          <div className="h-16" />
        </div>
      </div>
    </>
  );
};

export default Subscriptions;
